<template>
  <div
    class="hidden h-96 w-1/2 flex-none flex-col divide-y divide-gray-200 overflow-y-auto sm:flex xl:w-2/5"
  >
    <div class="flex flex-col gap-3 pb-4">
      <div class="flex flex-shrink-0 flex-row px-4">
        <div class="flex flex-1 flex-col">
          <h2
            class="cursor-pointer text-xl leading-tight font-semibold text-gray-900 capitalize dark:text-white"
            @click="$emit('click-option', activeOption)"
          >
            <span>
              {{ title }}
            </span>

            <span
              v-if="detail?.type?.name"
              class="ml-2 inline-flex flex-row items-center gap-x-1 rounded px-2 text-[10px] lowercase"
              :class="[
                `bg-theme-${detail.type.bg_color} text-${detail.type.text_color}`
              ]"
            >
              <FontAwesomeIcon :icon="['fas', detail.type.icon]" />

              {{ detail.type.name }}
            </span>
          </h2>
          <h3
            v-if="subtitle"
            class="text leading-tight font-semibold text-gray-400"
          >
            {{ subtitle }}
          </h3>
          <div
            v-if="activeOption.labels?.length"
            class="mt-1 flex flex-row flex-wrap items-center gap-1"
          >
            <span
              v-for="(label, labelIndex) in activeOption.labels"
              :key="labelIndex"
              :data-tint="label.color"
              class="shrink-0 rounded bg-primary px-1 text-xs text-contrast-primary"
            >
              {{ label.label }}
            </span>
          </div>
        </div>
        <div v-if="detail?.thumbnail && !thumbnailFailed" class="flex-shrink-0">
          <img
            :src="detail.thumbnail"
            alt=""
            class="h-20 w-20 rounded-md border-2 border-white object-cover ring-1 ring-gray-300"
            @error="thumbnailFailed = true"
          />
        </div>
        <div
          v-else-if="userInitials"
          class="flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-md border-2 border-white bg-linear-to-bl from-primary-500 to-primary-600 text-3xl font-bold text-contrast-primary ring-1 ring-gray-300"
          data-test="thumbnail-initials"
        >
          {{ userInitials }}
        </div>
      </div>
      <div
        v-if="quickContacts.length"
        class="flex flex-shrink-0 flex-row flex-wrap items-center gap-1 px-4"
      >
        <a
          v-for="(contact, contactIndex) in quickContacts"
          :key="contactIndex"
          :href="quickContactHref(contact)"
          class="inline-flex flex-row items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-slate-700 dark:text-gray-200 dark:hover:bg-slate-600"
          target="_blank"
          @click.stop
        >
          <FontAwesomeIcon
            fixed-width
            :icon="['fal', quickContactIcon(contact.type)]"
          />
          {{ contact.value }}
        </a>
      </div>
      <div v-if="loading && !detail" class="flex flex-col gap-2 px-4">
        <div
          class="h-3 w-2/3 animate-pulse rounded bg-gray-200 dark:bg-slate-600"
        />
        <div
          class="h-3 w-1/2 animate-pulse rounded bg-gray-200 dark:bg-slate-600"
        />
        <div
          class="h-3 w-3/4 animate-pulse rounded bg-gray-200 dark:bg-slate-600"
        />
      </div>
      <ViewData
        v-else
        :data="displayValues"
        size="extra-small"
        gap="medium"
        class="px-4"
      />
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { debounce } from '@u/debounce.js'
import ViewData from '@c/ViewComponents/ViewData.vue'

export default {
  name: 'InstantSearchPalettePreview',
  components: { FontAwesomeIcon, ViewData },
  props: {
    activeOption: {
      type: Object,
      required: true
    }
  },
  emits: ['click-option'],
  data() {
    return {
      detail: null,
      loading: false,
      abortController: null,
      lastKey: null,
      // the API gives an address for the photo of a user even when he has none: it then leads nowhere
      thumbnailFailed: false
    }
  },
  computed: {
    title() {
      return this.detail?.title ?? this.activeOption.title
    },
    subtitle() {
      return this.detail?.subtitle ?? this.activeOption.subtitle
    },
    displayValues() {
      return this.detail?.data ?? []
    },
    quickContacts() {
      return this.detail?.quick_contacts ?? []
    },
    // A user without a photo shows his initials, as the avatars of the application do
    userInitials() {
      if (this.activeOption.type !== 'user') {
        return ''
      }

      return (this.title ?? '')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word.charAt(0).toUpperCase())
        .join('')
    }
  },
  watch: {
    activeOption: {
      immediate: true,
      handler(newValue) {
        const key = `${newValue.type}-${newValue.id}`
        if (key === this.lastKey) {
          return
        }
        this.lastKey = key
        this.detail = null
        this.thumbnailFailed = false
        this.debouncedFetchDetail(newValue)
      }
    }
  },
  beforeCreate() {
    this.debouncedFetchDetail = debounce((option) => {
      this.fetchDetail(option)
    }, 150)
  },
  beforeUnmount() {
    if (this.abortController) {
      this.abortController.abort()
    }
  },
  methods: {
    ...mapActions({
      processGetInstantSearchDetailAction:
        'instantSearch/getInstantSearchDetail'
    }),
    fetchDetail(option) {
      if (this.abortController) {
        this.abortController.abort()
      }
      this.abortController = new AbortController()
      this.loading = true
      this.processGetInstantSearchDetailAction({
        type: option.type,
        id: option.id,
        signal: this.abortController.signal
      })
        .then((data) => {
          this.detail = data
        })
        .catch((reason) => {
          if (reason?.code !== 'ERR_CANCELED') {
            this.detail = null
          }
        })
        .finally(() => (this.loading = false))
    },
    quickContactIcon(type) {
      return (
        {
          email: 'envelope',
          telephone: 'phone',
          mobile: 'mobile'
        }[type] ?? 'address-card'
      )
    },
    quickContactHref(contact) {
      if (contact.type === 'email') {
        return `mailto:${contact.value}`
      }
      return `tel:${contact.value}`
    }
  }
}
</script>
